// Calculadora de calorías y macros — lógica pura (sin UI).
// Spec: docs/superpowers/specs/2026-06-13-calorie-macro-calculator-design.md

// Supuestos editables (ver spec). Hoy fijos como constantes.
export const DEFICIT_PCT = 0.2;        // déficit -20% para pérdida de grasa
export const PROTEIN_G_PER_KG = 2.2;            // default de proteína (piso del rango elegible)
export const FAT_G_PER_KG_MAINTENANCE = 0.7;    // grasa en mantenimiento
export const FAT_G_PER_KG_DEFICIT = 0.6;        // grasa en déficit (se baja antes que los carbos)

// Niveles de proteína elegibles por el usuario (g/kg), según masa muscular y experiencia.
export const PROTEIN_LEVELS = [
  { id: 'p22', label: '2.2 g/kg', value: 2.2, desc: 'Buena base muscular (punto de partida)' },
  { id: 'p23', label: '2.3 g/kg', value: 2.3, desc: 'Físico trabajado' },
  { id: 'p24', label: '2.4 g/kg', value: 2.4, desc: 'Bastante magro o mucha masa muscular' },
  { id: 'p25', label: '2.5 g/kg', value: 2.5, desc: 'Muy magro o déficit agresivo' },
];

// kcal por gramo de cada macro
export const KCAL_PER_G = { protein: 4, fat: 9, carbs: 4 };

// Umbral para avisar de carbos muy bajos (gramos)
export const LOW_CARB_THRESHOLD_G = 50;

// Niveles de actividad (menor a mayor). factor = multiplicador de Mifflin.
// Liderados por frecuencia de entreno + tipo de trabajo; pasos como dato secundario.
export const ACTIVITY_LEVELS = [
  {
    id: 'sedentario', label: 'Sedentario', factor: 1.2,
    trainings: '0-1 entrenos/sem', daily: '< 5.000 pasos, sentado todo el día',
  },
  {
    id: 'ligero', label: 'Ligero', factor: 1.375,
    trainings: '2-3 entrenos/sem', daily: '5.000 – 7.500 pasos',
  },
  {
    id: 'moderado', label: 'Moderado', factor: 1.55,
    trainings: '3-5 entrenos/sem', daily: '7.500 – 10.000 pasos',
  },
  {
    id: 'alto', label: 'Alto', factor: 1.725,
    trainings: '5-6 entrenos/sem', daily: '10.000 – 13.000 pasos o trabajo de pie',
  },
  {
    id: 'muy_alto', label: 'Muy alto', factor: 1.9,
    trainings: '5-6 entrenos/sem', daily: 'Trabajo físico pesado o doble sesión',
  },
];

// Parseo tolerante a coma decimal chilena. Devuelve NaN si no es número válido.
export function parseDecimal(value) {
  if (value === null || value === undefined) return NaN;
  const normalized = String(value).replace(',', '.').trim();
  if (normalized === '') return NaN;
  return parseFloat(normalized);
}

/**
 * BMR — Mifflin-St Jeor. Espera números ya parseados (usar parseDecimal antes).
 * @param {{sex:'male'|'female', age:number, heightCm:number, weightKg:number}} input
 */
// BMR — Mifflin-St Jeor. sex: 'male' | 'female'.
export function calcBMR({ sex, age, heightCm, weightKg }) {
  const base = 10 * weightKg + 6.25 * heightCm - 5 * age;
  return sex === 'male' ? base + 5 : base - 161;
}

// Calorías de mantenimiento (TDEE) = BMR × factor de actividad.
export function calcMaintenance({ sex, age, heightCm, weightKg, activityFactor }) {
  return calcBMR({ sex, age, heightCm, weightKg }) * activityFactor;
}

/**
 * Calorías en déficit. deficitPct es una fracción en [0,1), p.ej. 0.2 = 20%.
 */
// Calorías en déficit.
export function calcDeficit(maintenanceKcal, deficitPct = DEFICIT_PCT) {
  return maintenanceKcal * (1 - deficitPct);
}

// Macros para unas kcal y un peso dados.
// Proteína y grasa por peso corporal; carbos absorben el resto (nunca negativos).
export function calcMacros({
  kcal,
  weightKg,
  proteinGperKg = PROTEIN_G_PER_KG,
  fatGperKg = FAT_G_PER_KG_MAINTENANCE,
}) {
  const proteinG = proteinGperKg * weightKg;
  const fatG = fatGperKg * weightKg;
  const proteinKcal = proteinG * KCAL_PER_G.protein;
  const fatKcal = fatG * KCAL_PER_G.fat;
  const carbsKcal = Math.max(0, kcal - proteinKcal - fatKcal);
  const carbsG = carbsKcal / KCAL_PER_G.carbs;

  const totalKcal = proteinKcal + fatKcal + carbsKcal;
  const pct = (k) => (totalKcal > 0 ? (k / totalKcal) * 100 : 0);

  return {
    protein: { grams: proteinG, kcal: proteinKcal, pct: pct(proteinKcal) },
    carbs: { grams: carbsG, kcal: carbsKcal, pct: pct(carbsKcal) },
    fat: { grams: fatG, kcal: fatKcal, pct: pct(fatKcal) },
  };
}

/**
 * Resultado completo. Espera números ya parseados y válidos (la UI valida antes).
 */
// Resultado completo: mantenimiento + déficit, cada uno con sus macros.
export function computeResults({ sex, age, heightCm, weightKg, activityFactor, proteinGperKg = PROTEIN_G_PER_KG }) {
  const maintenanceKcal = calcMaintenance({ sex, age, heightCm, weightKg, activityFactor });
  const deficitKcal = calcDeficit(maintenanceKcal);
  return {
    maintenance: {
      kcal: maintenanceKcal,
      macros: calcMacros({ kcal: maintenanceKcal, weightKg, proteinGperKg, fatGperKg: FAT_G_PER_KG_MAINTENANCE }),
    },
    deficit: {
      kcal: deficitKcal,
      macros: calcMacros({ kcal: deficitKcal, weightKg, proteinGperKg, fatGperKg: FAT_G_PER_KG_DEFICIT }),
    },
  };
}
